import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import brandKitService from '../services/brandKitService';
import { useSiteContext } from './SiteContext';

const BrandKitContext = createContext();

export const useBrandKit = () => {
  const context = useContext(BrandKitContext);
  if (!context) {
    throw new Error('useBrandKit must be used within a BrandKitProvider');
  }
  return context;
};

// Maps a brand kit from the API to CSS custom properties
function applyBrandKitCSSVars(kit) {
  const root = document.documentElement;
  if (!kit) {
    // Clear custom properties when no kit
    root.style.removeProperty('--bk-primary');
    root.style.removeProperty('--bk-accent');
    root.style.removeProperty('--bk-background');
    root.style.removeProperty('--bk-text');
    root.style.removeProperty('--bk-secondary-text');
    root.style.removeProperty('--bk-font-heading');
    root.style.removeProperty('--bk-font-body');
    root.style.removeProperty('--bk-font-caption');
    root.style.removeProperty('--bk-logo-url');
    root.style.removeProperty('--bk-square-logo-url');
    return;
  }

  const colors = kit.colors || {};
  const fonts = kit.fonts || {};
  const logos = kit.logos || {};

  root.style.setProperty('--bk-primary', colors.primary || '#6a1fbf');
  root.style.setProperty('--bk-accent', colors.accent || '#00c853');
  root.style.setProperty('--bk-background', colors.background || '#001233');
  root.style.setProperty('--bk-text', colors.text || '#ffffff');
  root.style.setProperty('--bk-secondary-text', colors.secondary_text || '#9ca3af');
  root.style.setProperty('--bk-font-heading', fonts.heading || 'Inter');
  root.style.setProperty('--bk-font-body', fonts.body || 'Inter');
  root.style.setProperty('--bk-font-caption', fonts.caption || 'Inter');

  if (logos.main_logo_url) {
    root.style.setProperty('--bk-logo-url', `url(${logos.main_logo_url})`);
  } else {
    root.style.removeProperty('--bk-logo-url');
  }
  if (logos.square_logo_url) {
    root.style.setProperty('--bk-square-logo-url', `url(${logos.square_logo_url})`);
  } else {
    root.style.removeProperty('--bk-square-logo-url');
  }
}

// Converts a brand kit from the API into the meeting branding state shape
export function brandKitToMeetingBranding(kit) {
  if (!kit) return null;
  const colors = kit.colors || {};
  const fonts = kit.fonts || {};
  const logos = kit.logos || {};
  const frame = kit.frame_settings || {};
  const lowerThird = kit.lower_third || {};
  const overlay = kit.overlay_defaults || {};

  return {
    primaryColor: colors.primary || '#6a1fbf',
    accentColor: colors.accent || '#00c853',
    backgroundColor: colors.background || '#001233',
    font: fonts.heading || 'Inter',
    frame: frame.style || 'none',
    frameColor: frame.border_color || colors.primary || '#6a1fbf',
    frameBorderWidth: frame.border_width ?? 2,
    frameShadow: frame.shadow ?? true,
    gradientColor1: colors.primary || '#6a1fbf',
    gradientColor2: colors.accent || '#00c853',
    logoUrl: logos.main_logo_url || '',
    logoPosition: overlay.watermark_position || 'top-right',
    showLogo: !!logos.main_logo_url,
    lowerThirdTemplate: lowerThird.template || 'modern',
    lowerThirdName: '',
    lowerThirdTitle: '',
    showLowerThird: false,
    lowerThirdBackground: true,
    lowerThirdBgColor: lowerThird.background_color || '#000000',
    lowerThirdTextColor: lowerThird.text_color || '#ffffff',
    lowerThirdTitleColor: '#9ca3af',
    lowerThirdUnderline: false,
    lowerThirdBorder: false,
    lowerThirdBorderColor: lowerThird.accent_color || colors.primary || '#6a1fbf',
    lowerThirdShape: 'minimal',
    lowerThirdAnimation: 'none',
    qrUrl: '',
    showQrCode: false,
    qrPosition: 'bottom-right',
    qrColor: colors.background || '#001233',
    qrBgColor: '#ffffff',
    qrSize: 'medium',
    qrLogo: '',
  };
}

export const BrandKitProvider = ({ children }) => {
  const { isAuthenticated } = useSiteContext();
  const [activeBrandKit, setActiveBrandKit] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const fetchActiveBrandKit = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await brandKitService.getActiveBrandKit();
      const kit = res.data || null;
      setActiveBrandKit(kit);
      applyBrandKitCSSVars(kit);
      if (kit) {
        localStorage.setItem('rlink_active_brand_kit', JSON.stringify(kit));
      }
    } catch (err) {
      console.error('[BrandKitContext] Failed to load active brand kit:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setActiveBrandKit(null);
      applyBrandKitCSSVars(null);
      fetchedRef.current = false;
      return;
    }

    try {
      const cached = localStorage.getItem('rlink_active_brand_kit');
      if (cached) {
        const kit = JSON.parse(cached);
        setActiveBrandKit(kit);
        applyBrandKitCSSVars(kit);
      }
    } catch (e) { /* ignore */ }

    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchActiveBrandKit();
    }
  }, [isAuthenticated, fetchActiveBrandKit]);

  const refreshBrandKit = useCallback(async () => {
    fetchedRef.current = true;
    await fetchActiveBrandKit();
  }, [fetchActiveBrandKit]);

  const value = {
    activeBrandKit,
    loading,
    refreshBrandKit,
    getMeetingBranding: () => brandKitToMeetingBranding(activeBrandKit),
  };

  return (
    <BrandKitContext.Provider value={value}>
      {children}
    </BrandKitContext.Provider>
  );
};

export default BrandKitContext;
